import { useCallback, useMemo, useState } from "react"

import { LOCAL_STORAGE_KEYS } from "~config/constants"

type DismissedEntry = {
    readonly dismissedAt: number
    readonly dismissForSeconds: number | null
}

type DismissedMap = Record<string, DismissedEntry>

const STORAGE_KEY = LOCAL_STORAGE_KEYS.APP_PROMPT_DISMISSED

export default function useAppPromptDismissal() {
    const [dismissedMap, setDismissedMap] =
        useState<DismissedMap>(readFromStorage)
    const [sessionDismissals, setSessionDismissals] = useState<Set<string>>(
        () => new Set()
    )

    const dismiss = useCallback(
        (promptId: string, dismissForSeconds: number | null) => {
            if (dismissForSeconds === null) {
                setSessionDismissals((prev) => new Set([...prev, promptId]))
                return
            }

            setDismissedMap((prev) => {
                const next = {
                    ...prev,
                    [promptId]: {
                        dismissedAt: Date.now(),
                        dismissForSeconds,
                    },
                }
                writeToStorage(next)
                return next
            })
        },
        []
    )

    const isDismissed = useCallback(
        (promptId: string) => {
            if (sessionDismissals.has(promptId)) return true

            const entry = dismissedMap[promptId]
            if (!entry || entry.dismissForSeconds === null) return false

            const elapsedMs = Date.now() - entry.dismissedAt
            const cooldownMs = entry.dismissForSeconds * 1000

            return elapsedMs < cooldownMs
        },
        [dismissedMap, sessionDismissals]
    )

    return useMemo(() => ({ dismiss, isDismissed }), [dismiss, isDismissed])
}

function readFromStorage(): DismissedMap {
    if (typeof window === "undefined") return {}

    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : {}
    } catch {
        return {}
    }
}

function writeToStorage(map: DismissedMap) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
    } catch {
        // Storage full or unavailable
    }
}
